import React, {useCallback, useMemo, useState} from "react";
import {usePermission} from "./PermissionHooks";
import {service as ContextNodeService} from "../Services/ContextNodeService";
import {service as UserRoleService} from "../Services/UserRoleService";
import {usePrimeCache} from "./ServiceHooks";
import jmespath from "jmespath";
import {GenericModelUtils} from "../Utils/GenericModelUtils";
import {GuidUtils} from "../Utils";

export const useGuid = () => {
    const [editorGuid, setEditorGuid] = useState(() => GuidUtils.Generate());
    return editorGuid;
}

/**
 * 
 * @param initialState
 * @returns {[*[],function(*): void,boolean]}
 */
export const useValidations = (initialState = []) => {
    const [validations, setValidations] = useState(initialState);

    const isValid = Object.values(validations)?.every?.(validation => validation.valid) ?? true;

    // Return the raw setState so callers can use functional updaters and
    // React can bail out when the same reference is set (no-op re-render).
    // The old wrapper `(v) => setState({...v})` forced a new object on every
    // call, breaking functional updaters and causing unnecessary re-renders.
    return [
        validations,
        setValidations,
        isValid
    ];

}
export const useBuildContextNodeEditabilityElement = () => {

    usePrimeCache([UserRoleService]);
    
    return (contextNode, userRoleId) => {
        const userRole = UserRoleService.CachedValueById(userRoleId)?.result;    
        
        if(userRole == null || contextNode?.depth == null) {
            return null;
        }

        const editabilityMessage = ContextNodeService.GetEditabilityMessage(userRole, contextNode.depth);
        const editabilityElement = editabilityMessage ? <div className="sb-autocomplete-subtext">{editabilityMessage}</div> : null;

        return editabilityElement;
    }
}

export const useShouldEditField = () => {
    const permission = usePermission();

    return (props) => {
        // TODO: do we need to store these in state and
        // return a different thing when they change?
        const {EditMode, FieldDef, IsNewModel} = props;

        // Check the User Permissions
        if (
            FieldDef?.requiresPermission &&
            !permission(FieldDef?.requiresPermission)
        ) {
            return false;
        }

        return EditMode;
    }
}

export const useGenericModelHooks = (props) => {

    const {
        GetFieldDefs: getFieldDefs = ((includeHidden) => {}),
        UpdateField: updateField = ((value, field = {}) => {}),
        UpdateFields: updateFields = ((fields = []) => {}),
    } = props;

    const sortFieldDefs = (defs) => {
        if(defs) {
            defs = defs.sort((a, b) => {
                if(a.displayOrder && b.displayOrder) {
                    return a.displayOrder - b.displayOrder;
                } else if(a.displayOrder) {
                    return -1;
                } else if(b.displayOrder) {
                    return 1;
                } else {
                    return a.key.localeCompare(b.key);
                }
            });

            return defs;
        }

        return [];
    }

    return {
        getFieldDefs,
        updateField,
        updateFields,
        sortFieldDefs,
    }
}

export const useFieldDefinitionVisibility = (props) => {

    const hasPermissions = usePermission();
    const {
        EditMode,
        HasPermission,
        Data,
        RootData,
        CustomData
    } = props;

    const fieldDefinitionIsVisible = (field, hiddenFieldKeys = []) => {

        // if this field is a child of an association
        // it should be hidden if the association is hidden
        if(field.association && !fieldDefinitionIsVisible(field.association, hiddenFieldKeys)) {
            return false;
        }

        // hide based on viewerOnly and editorOnly rules
        if((EditMode ? field.viewerOnly : field.editorOnly)) {
            return false;
        }

        // hide nullable associations {
        if(GenericModelUtils.isHiddenNullableAssociationField(field, Data)) {
            return false;
        }

        // hide fields that are in the hiddenFieldKeys list
        if(hiddenFieldKeys?.includes?.(field.key)) {
            return false;
        }

        let isHidden = false;

        if(typeof field.isHidden == 'object') {
            const { query, requiresPermission } = field.isHidden;
            const searchContext = {
                ...Data,
                __RootData: RootData,
                __CustomData: CustomData,
                editMode: EditMode,
            }

            if(query) {
                isHidden = isHidden || jmespath.search(searchContext, query);
            }
            if(requiresPermission) {
                isHidden = isHidden || !hasPermissions(requiresPermission);
            }
        } else {
            isHidden = isHidden || field.isHidden;
        }

        return !isHidden;
    }

    return (groups, hiddenGroupKeys = [], hiddenFieldKeys = []) => groups
        .filter(group => (
            !hiddenGroupKeys ||
            !(hiddenGroupKeys.includes(group.key))
        ))
        .map(group => ({
            ...group,
            value: group.value.filter(
                fieldDef => fieldDefinitionIsVisible(fieldDef, hiddenFieldKeys)
            )
        })
    );
}

